import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import "./Addresses.css";

class Addresses extends Component {
  state = {
    addresses: [],
    formData: {
      id: null,
      address: "",
      address_status: "1",
    },
    deleteId: null,
    error: "",
    loading: false,
    submitting: false,
    deleting: false,
    modalMode: "add",
    searchTerm: "",
    statusFilter: "all",
    currentPage: 1,
    itemsPerPage: 10,
  };

  componentDidMount() {
    this.fetchAddresses();
  }

  fetchAddresses = async () => {
    this.setState({ loading: true, error: "" });

    try {
      const res = await axios.get("posts/addresses"); // Matches AddressModel table 'address'
      const data = res.data?.data || [];

      this.setState({
        addresses: Array.isArray(data) ? data : [],
        currentPage: 1,
      });
    } catch (err) {
      this.setState({
        error: err.response?.data?.message || "Failed to load addresses.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: { ...prev.formData, [name]: value },
      error: "",
    }));
  };

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value, currentPage: 1 });
  };

  handleStatusFilterChange = (e) => {
    this.setState({ statusFilter: e.target.value, currentPage: 1 });
  };

  handleItemsPerPageChange = (e) => {
    this.setState({ itemsPerPage: Number(e.target.value), currentPage: 1 });
  };

  clearFilters = () => {
    this.setState({ searchTerm: "", statusFilter: "all", currentPage: 1 });
  };

  openModal = (mode = "add", addressObj = null) => {
    const modalElement = document.getElementById("addressModal");
    if (!modalElement || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalElement);

    this.setState(
      {
        modalMode: mode,
        formData:
          mode === "edit" && addressObj
            ? {
                id: addressObj.id,
                address: addressObj.address || "",
                address_status: addressObj.address_status?.toString() || "1",
              }
            : {
                id: null,
                address: "",
                address_status: "1",
              },
        error: "",
      },
      () => modal.show()
    );
  };

  closeModal = () => {
    const modalElement = document.getElementById("addressModal");
    if (modalElement && window.bootstrap) {
      const modal = window.bootstrap.Modal.getInstance(modalElement);
      if (modal) modal.hide();
    }
    this.resetForm();
  };

  resetForm = () => {
    this.setState({
      formData: {
        id: null,
        address: "",
        address_status: "1",
      },
      error: "",
      submitting: false,
      modalMode: "add",
    });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;
    const addressText = formData.address.trim();

    if (!addressText) {
      return this.setState({ error: "Address content is required." });
    }

    this.setState({ submitting: true, error: "" });

    const payload = {
      address: addressText,
      address_status: formData.address_status,
    };

    try {
      if (modalMode === "add") {
        await axios.post("posts/addresses", payload);
      } else {
        await axios.put(`posts/addresses/${formData.id}`, payload);
      }

      await this.fetchAddresses();
      this.closeModal();

      Swal.fire({
        icon: "success",
        title:
          modalMode === "add"
            ? "Address added successfully!"
            : "Address updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      this.setState({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving address.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modalElement = document.getElementById("deleteModal");
    if (!modalElement || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalElement);
    modal.show();
  };

  handleDelete = async () => {
     
    const { deleteId } = this.state;
    if (!deleteId) return;

    this.setState({ deleting: true });

    try {
      await axios.delete(`posts/addresses/${deleteId}`);
      await this.fetchAddresses();

      const modalElement = document.getElementById("deleteModal");
      if (modalElement && window.bootstrap) {
        const modal = window.bootstrap.Modal.getInstance(modalElement);
        if (modal) modal.hide();
      }

      this.setState({ deleteId: null });

      Swal.fire({
        icon: "success",
        title: "Address deleted successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to delete address.",
        text: err.response?.data?.message || "Please try again.",
      });
    } finally {
      this.setState({ deleting: false });
    }
  };

  getFilteredAddresses = () => {
    const { addresses, searchTerm, statusFilter } = this.state;
    const keyword = searchTerm.trim().toLowerCase();

    return addresses.filter((item) => {
      const addressText = (item.address || "").toLowerCase();
      const status = item.address_status?.toString() || "0";

      const matchesSearch = !keyword || addressText.includes(keyword);
      const matchesStatus = statusFilter === "all" || status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  };

  getPageNumbers = (totalPages) => {
    const { currentPage } = this.state;
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

    const pages = [1];
    if (currentPage > 4) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (currentPage < totalPages - 3) pages.push("...");
    pages.push(totalPages);

    return pages;
  };

  goToPage = (page) => {
    const { currentPage, itemsPerPage } = this.state;
    const totalPages = Math.max(
      1,
      Math.ceil(this.getFilteredAddresses().length / itemsPerPage)
    );
    let nextPage = page;

    if (page === "prev") nextPage = currentPage - 1;
    if (page === "next") nextPage = currentPage + 1;
    if (typeof nextPage !== "number") return;

    nextPage = Math.min(totalPages, Math.max(1, nextPage));
    if (nextPage !== currentPage) this.setState({ currentPage: nextPage });
  };

  render() {
    const {
      addresses,
      formData,
      error,
      loading,
      submitting,
      deleting,
      modalMode,
      searchTerm,
      statusFilter,
      currentPage,
      itemsPerPage,
    } = this.state;

    const filteredAddresses = this.getFilteredAddresses();
    const totalPages = Math.max(
      1,
      Math.ceil(filteredAddresses.length / itemsPerPage)
    );
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * itemsPerPage;
    const currentAddresses = filteredAddresses.slice(
      startIndex,
      startIndex + itemsPerPage
    );
    const startRecord = filteredAddresses.length ? startIndex + 1 : 0;
    const endRecord = Math.min(startIndex + itemsPerPage, filteredAddresses.length);

    const activeCount = addresses.filter(
      (a) => a.address_status?.toString() === "1"
    ).length;
    const inactiveCount = addresses.length - activeCount;
    const pageNumbers = this.getPageNumbers(totalPages);

    return (
      <div className="pc-container addresses-page">
        <div className="pc-content">
          <div className="addresses-shell">
            {/* Header */}
            <div className="addresses-topbar">
              <div>
                <div className="addresses-breadcrumb">
                  <span>Settings</span>
                  <i className="ph ph-caret-right" />
                  <strong>Addresses</strong>
                </div>
                <h2 className="addresses-title">Addresses</h2>
                <p className="addresses-subtitle">
                  Manage company or user physical location records and statuses.
                </p>
              </div>

              <button
                type="button"
                className="bookly-primary-btn"
                onClick={() => this.openModal("add")}
              >
                <i className="ph ph-plus" />
                <span>New Address</span>
              </button>
            </div>

            {/* Summary Grid */}
            <div className="address-summary-grid">
              <div className="address-summary-card">
                <div className="address-summary-icon blue">
                  <i className="ph ph-map-pin" />
                </div>
                <div>
                  <span className="summary-label">Total Addresses</span>
                  <strong>{addresses.length}</strong>
                </div>
              </div>

              <div className="address-summary-card">
                <div className="address-summary-icon green">
                  <i className="ph ph-check-circle" />
                </div>
                <div>
                  <span className="summary-label">Active</span>
                  <strong>{activeCount}</strong>
                </div>
              </div>

              <div className="address-summary-card">
                <div className="address-summary-icon pink">
                  <i className="ph ph-pause-circle" />
                </div>
                <div>
                  <span className="summary-label">Inactive</span>
                  <strong>{inactiveCount}</strong>
                </div>
              </div>
            </div>

            {/* Main Card */}
            <div className="address-card">
              <div className="address-card-header">
                <div>
                  <h4>Address List</h4>
                  <p>
                    {filteredAddresses.length} result
                    {filteredAddresses.length !== 1 ? "s" : ""} found
                  </p>
                </div>
              </div>

              {/* Toolbar */}
              <div className="address-toolbar">
                <div className="address-search-wrap">
                  <i className="ph ph-magnifying-glass" />
                  <input
                    type="text"
                    className="address-search"
                    placeholder="Search address text..."
                    value={searchTerm}
                    onChange={this.handleSearchChange}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="address-search-clear"
                      onClick={() =>
                        this.setState({ searchTerm: "", currentPage: 1 })
                      }
                    >
                      <i className="ph ph-x" />
                    </button>
                  )}
                </div>

                <div className="address-filter-group">
                  <label htmlFor="addressStatusFilter">Status</label>
                  <select
                    id="addressStatusFilter"
                    className="address-filter-select"
                    value={statusFilter}
                    onChange={this.handleStatusFilterChange}
                  >
                    <option value="all">All Status</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

                <div className="address-filter-group">
                  <label htmlFor="addressRows">Rows</label>
                  <select
                    id="addressRows"
                    className="address-filter-select"
                    value={itemsPerPage}
                    onChange={this.handleItemsPerPageChange}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>

                {(searchTerm || statusFilter !== "all") && (
                  <button
                    type="button"
                    className="address-reset-btn"
                    onClick={this.clearFilters}
                  >
                    <i className="ph ph-arrow-counter-clockwise" />
                    Reset
                  </button>
                )}
              </div>

              {error && !loading && (
                <div className="address-alert">
                  <i className="ph ph-warning-circle" />
                  <span>{error}</span>
                  <button type="button" onClick={this.fetchAddresses}>
                    Retry
                  </button>
                </div>
              )}

              {loading ? (
                <div className="address-loading">
                  <div
                    className="spinner-border"
                    role="status"
                    aria-hidden="true"
                  />
                  <span>Loading addresses...</span>
                </div>
              ) : (
                <>
                  <div className="address-table-wrap">
                    <table className="address-table">
                      <thead>
                        <tr>
                          <th className="col-number">#</th>
                          <th>Address</th>
                          <th className="col-status">Status</th>
                          <th className="col-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentAddresses.length ? (
                          currentAddresses.map((item, index) => {
                            const isActive =
                              item.address_status === "1" ||
                              item.address_status === 1;
                            return (
                              <tr key={item.id}>
                                <td className="col-number table-index">
                                  {startIndex + index + 1}
                                </td>
                                <td>
                                  <div className="address-content-cell">
                                    <div className="address-badge-icon">
                                      <i className="ph ph-map-pin" />
                                    </div>
                                    <div>
                                      <span className="address-text-content">
                                        {item.address}
                                      </span>
                                      <small>ID #{item.id}</small>
                                    </div>
                                  </div>
                                </td>
                                <td className="col-status">
                                  <span
                                    className={`address-status-badge ${
                                      isActive ? "active" : "inactive"
                                    }`}
                                  >
                                    <span className="status-dot" />
                                    {isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td className="col-actions">
                                  <div className="address-actions">
                                    <button
                                      type="button"
                                      className="address-action-btn edit"
                                      onClick={() => this.openModal("edit", item)}
                                      title="Edit address"
                                    >
                                      <i className="ph ph-note-pencil" />
                                    </button>
                                    <button
                                      type="button"
                                      className="address-action-btn delete"
                                      onClick={() => this.confirmDelete(item.id)}
                                      title="Delete address"
                                    >
                                      <i className="ph ph-trash" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="4">
                              <div className="address-empty-state">
                                <div className="empty-icon">
                                  <i className="ph ph-map-pin" />
                                </div>
                                <h5>No addresses found</h5>
                                <p>
                                  {searchTerm || statusFilter !== "all"
                                    ? "Try changing your search query or filters."
                                    : "Add your first address to get started."}
                                </p>
                                {searchTerm || statusFilter !== "all" ? (
                                  <button
                                    type="button"
                                    className="address-reset-btn"
                                    onClick={this.clearFilters}
                                  >
                                    Clear Filters
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="bookly-secondary-btn"
                                    onClick={() => this.openModal("add")}
                                  >
                                    <i className="ph ph-plus" />
                                    Add Address
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredAddresses.length > 0 && (
                    <div className="address-pagination-wrap">
                      <div className="address-pagination-info">
                        Showing <strong>{startRecord}</strong> to{" "}
                        <strong>{endRecord}</strong> of{" "}
                        <strong>{filteredAddresses.length}</strong> addresses
                      </div>

                      {totalPages > 1 && (
                        <nav aria-label="Address pagination">
                          <ul className="address-pagination">
                            <li
                              className={
                                safeCurrentPage === 1 ? "disabled" : ""
                              }
                            >
                              <button
                                type="button"
                                onClick={() => this.goToPage("prev")}
                                disabled={safeCurrentPage === 1}
                              >
                                <i className="ph ph-caret-left" />
                              </button>
                            </li>
                            {pageNumbers.map((page, index) =>
                              page === "..." ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="ellipsis"
                                >
                                  <span>...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={
                                    safeCurrentPage === page ? "active" : ""
                                  }
                                >
                                  <button
                                    type="button"
                                    onClick={() => this.goToPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              )
                            )}
                            <li
                              className={
                                safeCurrentPage === totalPages ? "disabled" : ""
                              }
                            >
                              <button
                                type="button"
                                onClick={() => this.goToPage("next")}
                                disabled={safeCurrentPage === totalPages}
                              >
                                <i className="ph ph-caret-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        <div className="modal fade" id="addressModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content address-modal">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header address-modal-header">
                  <div>
                    <span className="address-modal-kicker">
                      {modalMode === "add" ? "Create" : "Update"}
                    </span>
                    <h5 className="modal-title">
                      {modalMode === "add" ? "Add Address" : "Edit Address"}
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="address-modal-close"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                  >
                    <i className="ph ph-x" />
                  </button>
                </div>

                <div className="modal-body address-modal-body">
                  {error && (
                    <div className="address-form-alert">
                      <i className="ph ph-warning-circle" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="address-form-group">
                    <label htmlFor="addressText">
                      Address <span>*</span>
                    </label>
                    <div className="address-input-wrap">
                      <i className="ph ph-map-pin" />
                      <textarea
                        id="addressText"
                        name="address"
                        className="address-textarea"
                        placeholder="Enter full address details..."
                        value={formData.address}
                        onChange={this.handleChange}
                      />
                    </div>
                  </div>

                  <div className="address-form-group mb-0">
                    <label>Status</label>
                    <div className="address-status-options">
                      <label
                        className={`status-option ${
                          formData.address_status === "1" ? "selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="address_status"
                          value="1"
                          checked={formData.address_status === "1"}
                          onChange={this.handleChange}
                        />
                        <span>
                          <i className="ph ph-check-circle" /> Active
                        </span>
                      </label>
                      <label
                        className={`status-option ${
                          formData.address_status === "0" ? "selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="address_status"
                          value="0"
                          checked={formData.address_status === "0"}
                          onChange={this.handleChange}
                        />
                        <span>
                          <i className="ph ph-pause-circle" /> Inactive
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="modal-footer address-modal-footer">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bookly-primary-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i
                          className={
                            modalMode === "add" ? "ph ph-plus" : "ph ph-check"
                          }
                        />
                        {modalMode === "add" ? "Add Address" : "Update Address"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Delete Modal */}
        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content delete-modal">
              <div className="delete-modal-body">
                <div className="delete-warning-icon">
                  <i className="ph ph-trash" />
                </div>
                <h5>Delete Address?</h5>
                <p>
                  This address will be permanently removed. This action cannot be
                  undone.
                </p>
                <div className="delete-modal-actions">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bookly-danger-btn"
                    onClick={this.handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="ph ph-trash" />
                        Yes, Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Addresses;
